/**
 * PATH: packages/bills/src/components/DocumentViewer.tsx
 * FILE_ID: BILLS-PKG-CMP-DOC-001
 * PURPOSE: Centre pane — render the source PDF / image of the bill with
 *          working trackpad-pinch zoom on macOS.
 *
 * WHY: Mac trackpad pinches fire `wheel` events with `ctrlKey=true` on
 *      whichever element is under the pointer. When that's our outer
 *      wrapper, the browser zooms the whole page instead of the PDF
 *      (jake 2026-05-05 — "can't pinch zoom the bill"). We intercept
 *      ctrl+wheel, translate it into a numeric zoom level, and rebuild
 *      the iframe `src` fragment with `zoom=<n>` (Chromium PDF viewer
 *      respects this). For images we use CSS `transform: scale(...)`.
 *      Explicit zoom buttons cover keyboard / button-press users.
 *
 *      Two auth models: cookie (apps/billing → `attachmentUrl()`) and
 *      bearer (apps/sam → `fetchAttachmentBlob()` → blob URL). Prefer
 *      the blob path when both exist (always auth-correct).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertTriangle,
  Crosshair,
  Download,
  FileSearch,
  Loader2,
  Minus,
  Plus,
  RotateCw,
  X,
} from 'lucide-react';
import { Button } from '@quotech/ui';
import { useBillsClient } from '../client';
import type { BillDetail, BillFieldRegion } from '../types';
import type { BillFieldKey } from './billFieldKeys';
import type { BillRegionValues } from './pdfFieldRegions';
import { DEFAULT_ZOOM, clampZoom, nextZoom } from './zoomLadder';
import { DocxBlobViewer } from './DocxBlobViewer';
import { PdfCanvasViewer } from './PdfCanvasViewer';
import { Empty } from './viewerPrimitives';
import { useDragPan } from './useDragPan';

interface Props {
  bill: BillDetail | null;
  loading: boolean;
  /**
   * Live values override for the derived highlight regions — the workspace
   * passes the merged (bill + unsaved draft) preview so the PDF boxes track
   * edits as they're typed. Null/absent → regions derive from ``bill``.
   */
  regionValues?: BillRegionValues | null;
  /**
   * Optional controls injected into the header's action cluster (left of
   * the zoom buttons) by the host workspace — e.g. the "Email" split
   * toggle. Kept generic so DocumentViewer stays unaware of bill panels.
   */
  headerSlot?: ReactNode;
  /**
   * Currently-selected field for read↔form↔document cross-highlight (1c/
   * 1d). When the extractor has emitted ``field_regions`` (1d) the PDF
   * viewer draws a highlight box over the matching region; otherwise this
   * is inert (the read/form highlight still works).
   */
  activeField?: BillFieldKey | null;
  /** Report a click on a highlight box back up so the read/form panes
   *  can sync their own selection. */
  onFieldSelect?: (field: BillFieldKey | null) => void;
  /**
   * "Pick from PDF" (p1e). When ``onPick`` is provided the PDF is ALWAYS
   * pickable: hovering reveals the text runs, a click grabs the run under the
   * cursor and reports it via ``onPick``. The workspace fills the armed field,
   * or stashes the value for the next field (value-first). There is no mode to
   * toggle — this was the recurring confusion (see CapturePageLayer).
   * ``regionField`` = the field whose highlight box the gesture started in
   * (null off-region), so an un-armed pick can route straight to that field.
   */
  onPick?: (
    text: string,
    regionField?: BillFieldKey | null,
    gesture?: 'click' | 'drag',
  ) => void;
  /**
   * In-place edit session on the PDF (see PdfInlineFieldEditor): the field
   * being edited + the prefill value. Owned by the workspace so the commit
   * flows through the same captured→draft→autosave pipeline as picks.
   */
  inlineEdit?: { field: BillFieldKey; value: string; nonce: number } | null;
  onInlineEditCommit?: (field: BillFieldKey, value: string) => void;
  onInlineEditCancel?: () => void;
  /** Humanised name of the field currently armed to receive a pick, or null. */
  captureLabel?: string | null;
  /** A value already grabbed off the doc but not yet dropped into a field. */
  pickedValue?: string | null;
  /** Esc / Clear → drop the armed field + any stashed value. */
  onCancelCapture?: () => void;
}

interface ResolvedSource {
  url: string;
  /** True when `url` is a `blob:` URL we created and must revoke. */
  ownsBlobUrl: boolean;
  /**
   * The raw bytes when we fetched a blob ourselves. Needed by the docx
   * branch — mammoth wants an `ArrayBuffer`, not a URL — but kept null
   * for the cookie-auth path because we never materialise the bytes
   * client-side there.
   */
  blob: Blob | null;
}

export function DocumentViewer({
  bill,
  loading,
  regionValues,
  headerSlot,
  activeField,
  onFieldSelect,
  onPick,
  inlineEdit,
  onInlineEditCommit,
  onInlineEditCancel,
  captureLabel,
  pickedValue,
  onCancelCapture,
}: Props) {
  const client = useBillsClient();
  const attachmentId = bill?.attachment_id ?? null;
  const regions: BillFieldRegion[] = useMemo(
    () => (Array.isArray(bill?.field_regions) ? bill!.field_regions : []),
    [bill],
  );

  // Resolve source URL. Prefer blob fetch when the app provides it,
  // because that path is auth-correct under bearer tokens.
  const [source, setSource] = useState<ResolvedSource | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  // Bumped by the error-state Retry button to re-run the resolve effect, so a
  // transient attachment-fetch failure is recoverable without reselecting.
  const [retryNonce, setRetryNonce] = useState(0);
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Reset zoom when the selected bill changes — otherwise jumping to
  // a portrait scan after looking at a 200%-zoomed landscape one
  // leaves the new doc oversized and offscreen.
  useEffect(() => {
    setZoom(DEFAULT_ZOOM);
  }, [bill?.id]);

  // Compute the static URL synchronously — only used as a fallback and
  // for the "Open in new tab" link.
  const staticUrl = useMemo(
    () => client.attachmentUrl?.(attachmentId) ?? null,
    [client, attachmentId],
  );

  useEffect(() => {
    let cancelled = false;
    let createdBlobUrl: string | null = null;

    async function resolve() {
      if (!attachmentId) {
        setSource(null);
        setResolveError(null);
        return;
      }
      if (typeof client.fetchAttachmentBlob === 'function') {
        setResolving(true);
        setResolveError(null);
        try {
          const blob = await client.fetchAttachmentBlob(attachmentId);
          if (cancelled) return;
          if (!blob) { setSource(null); return; }
          createdBlobUrl = URL.createObjectURL(blob);
          setSource({ url: createdBlobUrl, ownsBlobUrl: true, blob });
        } catch (e) {
          if (cancelled) return;
          setResolveError(e instanceof Error ? e.message : 'Failed to load document');
          setSource(null);
        } finally {
          if (!cancelled) setResolving(false);
        }
        return;
      }
      if (staticUrl) setSource({ url: staticUrl, ownsBlobUrl: false, blob: null });
      else setSource(null);
    }

    resolve();
    return () => {
      cancelled = true;
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
    };
  }, [client, attachmentId, staticUrl, retryNonce]);

  // Intercept ctrl/⌘+wheel (== macOS trackpad pinch) BEFORE the browser
  // does native page zoom — Mac users would otherwise scale the whole
  // dashboard chrome.
  //
  // This MUST be a CALLBACK REF, not a useEffect([]). The scroll wrapper is
  // rendered only AFTER the attachment blob resolves asynchronously, so a
  // mount-time effect runs while `wrapperRef.current` is still null, bails,
  // and never re-attaches — which is exactly why trackpad pinch silently
  // did nothing (jake's original effect predated the async source resolve).
  // A callback ref fires precisely when the node mounts/unmounts, so the
  // non-passive listener is always bound to the live element. (Non-passive
  // is required so preventDefault actually cancels native page zoom — React's
  // onWheel is passive on modern Chrome and can't.)
  const wheelCleanup = useRef<(() => void) | null>(null);
  const attachWrapper = useCallback((el: HTMLDivElement | null) => {
    wrapperRef.current = el;
    wheelCleanup.current?.();
    wheelCleanup.current = null;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      setZoom((z) => clampZoom(nextZoom(z, e.deltaY < 0 ? 1 : -1)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    wheelCleanup.current = () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handleZoomIn = useCallback(() => setZoom((z) => nextZoom(z, 1)), []);
  const handleZoomOut = useCallback(() => setZoom((z) => nextZoom(z, -1)), []);
  const handleZoomReset = useCallback(() => setZoom(DEFAULT_ZOOM), []);

  // Drag-to-pan once the content can overflow the pane (zoom > fit). The pick
  // overlay distinguishes a click (pick) from a drag (pan) itself, so the two
  // coexist — no need to disable panning while picking.
  useDragPan(wrapperRef, zoom > DEFAULT_ZOOM);

  // Esc clears the armed field / stashed pick (mirrors the drawer's Esc habit).
  const hasArmedOrStash = Boolean(captureLabel) || Boolean(pickedValue);
  useEffect(() => {
    if (!hasArmedOrStash) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancelCapture?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasArmedOrStash, onCancelCapture]);

  if ((loading && !bill) || resolving) {
    return (
      <Empty>
        <div className="flex flex-col items-center gap-3">
          <span
            aria-hidden
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-muted text-muted-foreground"
          >
            <Loader2 className="h-5 w-5 animate-spin" />
          </span>
          <span className="text-sm text-muted-foreground">Loading document…</span>
        </div>
      </Empty>
    );
  }
  if (!bill) {
    return (
      <Empty>
        <div className="flex max-w-[16rem] flex-col items-center gap-3">
          <span
            aria-hidden
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-muted text-muted-foreground"
          >
            <FileSearch className="h-5 w-5" />
          </span>
          <p className="text-sm font-medium text-foreground">No document selected</p>
          <p className="text-xs text-muted-foreground">
            Select a bill to preview the source document.
          </p>
        </div>
      </Empty>
    );
  }
  if (resolveError) {
    return (
      <Empty>
        <div className="flex max-w-sm flex-col items-center gap-3">
          <span
            aria-hidden
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-destructive/10 text-destructive"
          >
            <AlertTriangle className="h-5 w-5" />
          </span>
          <p className="text-sm font-medium text-foreground">Could not load document.</p>
          <p className="text-xs text-destructive">{resolveError}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRetryNonce((n) => n + 1)}
            className="mt-1 gap-1.5 rounded-lg"
          >
            <RotateCw className="h-3.5 w-3.5" aria-hidden />
            Retry
          </Button>
        </div>
      </Empty>
    );
  }
  if (!source) {
    return (
      <Empty>
        <div className="flex max-w-sm flex-col items-center gap-3">
          <span
            aria-hidden
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-muted text-muted-foreground"
          >
            <FileSearch className="h-5 w-5" />
          </span>
          <p className="text-sm font-medium text-foreground">
            {attachmentId
              ? 'Document preview is not configured for this app.'
              : 'No source attachment found for this bill.'}
          </p>
          <p className="text-xs text-muted-foreground">
            Email subject:{' '}
            <span className="font-medium text-foreground">{bill.email_subject || '—'}</span>
          </p>
        </div>
      </Empty>
    );
  }

  const contentType = (bill.attachment_content_type || '').toLowerCase();
  const filename = (bill.attachment_filename || '').toLowerCase();
  const isPdf = contentType === 'application/pdf' || filename.endsWith('.pdf');
  const isImage =
    contentType.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/.test(filename);
  // Modern Word: any of the docx mime variants (ms-word vs OOXML) OR
  // the .docx extension. We deliberately do NOT match `.doc` here —
  // legacy binary .doc isn't supported by mammoth and needs a separate
  // server-side conversion path; calling it out below as a clear
  // fallback is more useful than rendering an unhelpful error.
  const isDocx =
    contentType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    filename.endsWith('.docx');
  const isLegacyDoc =
    contentType === 'application/msword' ||
    (filename.endsWith('.doc') && !filename.endsWith('.docx'));
  const externalUrl = staticUrl || source.url;

  // Always-on pick guidance for PDFs. Three states drive the copy:
  //   armed   → a field is waiting for its value
  //   stashed → a value was grabbed but no field chosen yet (value-first)
  //   idle    → teach the two ways in (click a value, or click a field)
  const pickable = isPdf && Boolean(onPick);

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col bg-muted/40">
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-card px-4 py-2.5 text-sm">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-foreground">
            {bill.attachment_filename || 'Source document'}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {bill.vendor_name || '—'} ·{' '}
            {bill.invoice_number ? `Bill #${bill.invoice_number}` : 'no bill # extracted'}
          </div>
        </div>
        {/*
          Action cluster must WRAP within the centre pane, not overflow it.
          On a narrow pane (wide right rail + left list) a non-wrapping
          shrink-0 row pushed the zoom buttons off the right edge so the
          operator literally couldn't reach "+" ("I can't zoom in?").
          `min-w-0 flex-wrap` lets the buttons flow onto a second line
          instead of clipping; on a wide pane it stays a single row.
        */}
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
          {headerSlot}
          {/* Segmented zoom control — one soft track behind ghost − / % / +
              buttons so the trio reads as a single unit (matches the modern
              redesign's segmented-control language). */}
          <div className="inline-flex shrink-0 items-center gap-0.5 rounded-lg border border-border bg-card p-0.5 shadow-sm">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              title="Zoom out (⌘−)"
              aria-label="Zoom out"
              className="h-7 w-7 rounded-md p-0 text-muted-foreground hover:text-foreground"
            >
              <Minus className="h-3.5 w-3.5" aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomReset}
              title="Reset zoom (⌘0)"
              aria-label="Reset zoom"
              className="h-7 min-w-[3rem] rounded-md px-1.5 text-center text-xs font-medium tabular-nums text-foreground"
            >
              {zoom}%
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              title="Zoom in (⌘+)"
              aria-label="Zoom in"
              className="h-7 w-7 rounded-md p-0 text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </div>
          {/* Download the source document. Opening a `blob:` URL in a new tab
              proved unreliable in this shell — an isolated-context navigation
              mangled the blob URL (operator saw "https://https//blob:…") — so
              we trigger a real FILE DOWNLOAD from the fetched bytes instead:
              no navigation, no popup, nothing to mangle. A `<a download>` on a
              fresh object URL works everywhere; a real server URL (cookie
              path) is downloaded directly. */}
          <button
            type="button"
            title="Download the source document"
            onClick={() => {
              const madeBlobUrl = !staticUrl && !!source.blob;
              const href = staticUrl || (source.blob ? URL.createObjectURL(source.blob) : source.url);
              if (!href) return;
              const a = document.createElement('a');
              a.href = href;
              a.download = bill.attachment_filename || 'document';
              a.rel = 'noopener';
              document.body.appendChild(a);
              a.click();
              a.remove();
              if (madeBlobUrl) setTimeout(() => URL.revokeObjectURL(href), 10_000);
            }}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            <span className="hidden sm:inline">Download</span>
          </button>
        </div>
      </header>

      {pickable && (
        <div
          className={[
            'flex shrink-0 items-center justify-between gap-2 border-b px-4 py-1.5 text-xs',
            captureLabel
              ? 'border-primary/30 bg-primary/10 text-primary'
              : pickedValue
                ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
                : 'border-border bg-muted/40 text-muted-foreground',
          ].join(' ')}
        >
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <Crosshair className="h-3.5 w-3.5 shrink-0" />
            {captureLabel ? (
              <span className="truncate">
                Drag across the document text to copy it into{' '}
                <strong>{captureLabel}</strong> — clicks never overwrite
              </span>
            ) : pickedValue ? (
              <span className="truncate">
                Grabbed <strong>“{pickedValue}”</strong> — now click the field to drop it in
              </span>
            ) : (
              <span className="truncate">
                Click a highlighted value to edit it in place — or click other text
                to grab it, then click the field to drop it in
              </span>
            )}
          </span>
          {(captureLabel || pickedValue) && (
            <button
              type="button"
              onClick={onCancelCapture}
              title="Clear (Esc)"
              className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <X className="h-3.5 w-3.5" aria-hidden /> Clear
            </button>
          )}
        </div>
      )}

      {/* Wrapper carries the wheel-intercept (ctrl+wheel → zoom) listener
          and is the single scroll/pan container for every renderer. */}
      <div
        ref={attachWrapper}
        className="min-h-0 flex-1 overflow-auto p-3"
        style={{ overscrollBehavior: 'contain' }}
      >
        {isPdf ? (
          <PdfCanvasViewer
            source={source}
            zoom={zoom}
            cacheKey={bill.id}
            viewportRef={wrapperRef}
            regions={regions}
            regionValues={regionValues ?? bill}
            baseValues={bill}
            activeField={activeField ?? null}
            onRegionClick={onFieldSelect}
            onPick={onPick}
            inlineEdit={inlineEdit}
            onInlineEditCommit={onInlineEditCommit}
            onInlineEditCancel={onInlineEditCancel}
            pannable={zoom > DEFAULT_ZOOM}
          />
        ) : isImage ? (
          <div className="flex h-full w-full items-start justify-center rounded-lg border border-border bg-background">
            {/*
              `object-fit: contain` is the actual fit-to-pane primitive
              — preserves aspect ratio AND clamps to the wrapper. The
              `transform: scale(...)` layers the user's explicit zoom
              on top of the fitted baseline.
            */}
            <img
              src={source.url}
              alt={bill.attachment_filename || 'Bill source image'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center',
                transition: 'transform 80ms ease-out',
              }}
            />
          </div>
        ) : isDocx ? (
          <DocxBlobViewer
            blob={source.blob}
            zoom={zoom}
            cacheKey={bill.id}
            filename={bill.attachment_filename || 'document.docx'}
            downloadUrl={externalUrl}
          />
        ) : isLegacyDoc ? (
          <Empty>
            <p>Legacy Word format (.doc) — inline preview not yet supported.</p>
            <p className="mt-1 text-xs">
              Open the original or ask the sender to resend as .docx.
            </p>
            <a
              href={externalUrl}
              target="_blank"
              rel="noreferrer"
              download={bill.attachment_filename || undefined}
              className="mt-2 inline-block text-primary underline"
            >
              Download original
            </a>
          </Empty>
        ) : (
          <Empty>
            <p>Unsupported preview format.</p>
            <a
              href={externalUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-primary underline"
            >
              Download original
            </a>
          </Empty>
        )}
      </div>

    </div>
  );
}

